import React, { useContext } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Icon, Dropdown, Popup } from 'semantic-ui-react';
import DataContext from '../../../contexts/DataContext';
import RescheduleMeeting from '../RescheduleMeeting';
import CancelJob from '../CancelJob';
import CancelInvite from './CancelInvite';
import ReInviteExpert from './ReInviteExpert';
import ReportNoShow from '../ReportNoShow';
import PayBonusModal from '../../Transactions/PayBonusModal';
import { MEETING_INVITE } from '../../../queries/meeting';
import addToCalendar from '../../../utils/addToCalendar';
import Security from '../../../utils/Security';

const FIVE_HOURS_AGO = new Date(new Date().getTime() - 5 * 60000);

const MeetingSettings = ({ job }) => {
  const { user, team } = useContext(DataContext);
  const [handleAddToCalendar] = useLazyQuery(MEETING_INVITE, {
    variables: { id: job.id },
    onCompleted: (data) => addToCalendar({
      job: {
        ...job,
        qualificationResponses: data.job.qualificationResponses,
        project: {
          ...job.project,
          preInterviewLink: data.job.project.preInterviewLink,
        },
        zoomMeeting: {
          ...job.zoomMeeting,
          meetingLink: data.job.zoomMeeting.meetingLink,
          meetingInvite: data.job.zoomMeeting.meetingInvite,
        },
      },
      user,
      isDoubleBlinded: team.isDoubleBlinded,
    }),
  });
  const isProjectOwner = Security.isProjectOwner(job.project);
  const isDecibio = Security.isDecibio();
  const isSuperUser = Security.isSuperUser();

  const renderPayBonusButton = () => {
    if (job.expert.stripeAccount.isConnected) {
      return (
        <PayBonusModal job={job}>
          <Dropdown.Item as="a">
            Pay bonus
          </Dropdown.Item>
        </PayBonusModal>
      );
    }
    return (
      <Dropdown.Item className="disabled-btn">
        Pay bonus
      </Dropdown.Item>
    );
  };

  const renderScheduledOptions = () => {
    if (job.meetingAt) {
      if (job.meetingAt < FIVE_HOURS_AGO) {
        return (
          <ReportNoShow job={job}>
            <Dropdown.Item as="a">Report a no show</Dropdown.Item>
          </ReportNoShow>
        );
      }
      if (job.meetingAt > new Date()) {
        return (
          <>
            <Dropdown.Item as="a" onClick={handleAddToCalendar}>Add to calendar</Dropdown.Item>
            {isProjectOwner && (
              <>
                <CancelJob job={job}>
                  <Dropdown.Item as="a">Cancel this meeting</Dropdown.Item>
                </CancelJob>
                <RescheduleMeeting job={job}>
                  <Dropdown.Item as="a">Reschedule this meeting</Dropdown.Item>
                </RescheduleMeeting>
              </>
            )}
          </>
        );
      }
    }
    return (
      <CancelJob job={job}>
        <Dropdown.Item as="a">Cancel this invite</Dropdown.Item>
      </CancelJob>
    );
  };

  return (
    <Dropdown
      trigger={<Icon id="ellipsis-menu" name="ellipsis vertical" />}
      pointing="top right"
      id="meeting-settings"
    >
      <Dropdown.Menu onKeyDown={(e) => e.stopPropagation()}>
        {(job.overall_status === 'Pending Scheduling' || job.overall_status === 'Client Reschedule') && (
          <CancelInvite job={job}>
            <Dropdown.Item as="a">Cancel this request</Dropdown.Item>
          </CancelInvite>
        )}
        {job.overall_status === 'Scheduled' && renderScheduledOptions()}
        {job.overall_status === 'Expired' && (
          <ReInviteExpert job={job}>
            <Dropdown.Item as="a">
              Re-invite this expert
            </Dropdown.Item>
          </ReInviteExpert>
        )}
        {/* Remove condition if we want to allow clients to make bonus payments */}
        {(isDecibio || isSuperUser) && (
          <Popup
            disabled={job.expert.stripeAccount.isConnected}
            content="Bonus is only available to Stripe-connected experts"
            trigger={renderPayBonusButton()}
          />
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MeetingSettings;
